/* import __COLOCATED_TEMPLATE__ from './predicates-editor.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import Predicate from 'predicates/models/predicate';
import { DataType } from 'embercom/objects/inbox/conversation-attribute-descriptor';

export default class PredicatesEditor extends Component {
  @service appService;
  @service attributeService;
  @service('filterable-attributes') filterableAttributesService;
  @service intl;
  @tracked predicates;
  @tracked paywall;
  @tracked _isLoadingUnifiedAttributes = false;
  @tracked _useUnifiedAttributes = false;

  constructor() {
    super(...arguments);

    // Pre-load unified attributes if feature flag is enabled
    if (this.appService.app?.canUseFeature('team-inbox-unified-filterable-attributes')) {
      this._loadUnifiedAttributes();
    }

    if (this.args.setSupportedAttributes) {
      this.args.setSupportedAttributes(this.supportedPredicateAttributes);
    }
  }

  async _loadUnifiedAttributes() {
    if (this._isLoadingUnifiedAttributes) {
      return;
    }

    this._isLoadingUnifiedAttributes = true;

    try {
      // Check if already loaded to avoid unnecessary network calls
      if (this.filterableAttributesService.isLoadedForContext('view')) {
        this._useUnifiedAttributes = true;
        this._isLoadingUnifiedAttributes = false;
        return;
      }

      await this.filterableAttributesService.loadForContext('view');
      // Only enable unified attributes after successful load
      this._useUnifiedAttributes = true;
    } catch (error) {
      console.error('Failed to load unified filterable attributes in predicates-editor:', error);
      // Fallback to legacy attributes (already available synchronously)
      this._useUnifiedAttributes = false;
    } finally {
      this._isLoadingUnifiedAttributes = false;
    }
  }

  get supportedPredicateAttributes() {
    return this.attributeGroupList
      .map((group) => group.attributes)
      .flat()
      .map((attribute) => attribute.identifier);
  }

  get attributeGroupList() {
    // Use unified filterable attributes only after initial load is complete
    // This prevents switching between legacy and new attributes mid-session
    if (
      this.appService.app?.canUseFeature('team-inbox-unified-filterable-attributes') &&
      this._useUnifiedAttributes
    ) {
      // This getter accesses @tracked properties in the service, so it will auto-track
      let groups = this.filterableAttributesService.attributeGroupListForViews;

      // Apply paywall wrapper to next_breach_time attribute if needed
      if (this.paywall?.isActive) {
        groups = groups.map((group) => {
          let updatedAttributes = group.attributes.map((attribute) => {
            if (attribute.identifier === 'conversation.next_breach_time') {
              return {
                ...attribute,
                component: 'paywalls/select-item',
                componentShouldReplaceItem: false,
                paywallOpenUpgradeModal: this.paywall?.openUpgradeModal,
                paywallAnalyticsEventData: this.paywall?.analyticsEventData,
              };
            }
            return attribute;
          });
          return { ...group, attributes: updatedAttributes };
        });
      }

      return groups;
    }

    // Legacy implementation (used on first render while loading, or if feature flag disabled/load failed)
    let groupList = [];

    let attributes = this.attributeService.inboxViewsAttributes.map((attribute) => {
      if (attribute.identifier === 'conversation.next_breach_time' && this.paywall?.isActive) {
        return {
          ...attribute,
          component: 'paywalls/select-item',
          componentShouldReplaceItem: false,
          paywallOpenUpgradeModal: this.paywall?.openUpgradeModal,
          paywallAnalyticsEventData: this.paywall?.analyticsEventData,
        };
      } else {
        return attribute;
      }
    });

    groupList.push(
      { attributes },
      this.conversationAttributes,
      ...this.attributeService.ticketAttributesByType,
    );

    return groupList;
  }

  get conversationAttributes() {
    let attributesUsed = [];
    for (let predicate of Predicate.walkPredicates(this.args.predicateGroup.predicates)) {
      predicate.attributeIdentifier && attributesUsed.push(predicate.attributeIdentifier);
    }

    let usedArchivedConversationCustomAttributes =
      this.attributeService.archivedConversationCustomAttributes.filter((attribute) =>
        attributesUsed.includes(attribute.identifier),
      );

    let allConversationDataAttributes = [
      ...this.attributeService.nonArchivedConversationCustomAttributes,
      ...usedArchivedConversationCustomAttributes,
    ].reject((attribute) => attribute.type === DataType.Files || attribute.hideFromInboxViews);

    return {
      heading: this.intl.t('inbox.inbox-views.predicates.conversation-attribute-heading'),
      attributes: allConversationDataAttributes,
    };
  }
}
